import { Button } from "@svea/design-system-components-react";
import { cloneElement } from "react";
import { useText } from "../../hooks";
import type { QuestionSetItem } from "../../types/question-set";
import { Expandable } from "../expandable";
import { Markdown } from "../markdown";
import { Stack } from "../stack";
import { Heading } from "../typography";
import useQuestionSetState from "./hooks";
import QuestionStep from "./question-step";
import StepContent from "./step-content";
import StepLabel from "./step-label";
import { createKeyHandler } from "./utils";

type Props = {
	questions: Array<QuestionSetItem>;
	isEditable?: boolean;
};

export const QuestionSet = ({ questions, isEditable = true }: Props) => {
	const { currentOpenStep, setOpenStep, isStepDisabled, nextOpenStep } =
		useQuestionSetState(questions);

	const getKey = createKeyHandler();

	const [t, texts] = useText();

	return (
		<Stack>
			{questions.map((question, step) => {
				const { heading, description, component, isCompleted } = question;
				const isExpanded = currentOpenStep === step;

				const isDisabled = isStepDisabled(step);

				const toggleIsExpanded = () => {
					if (isEditable && !isDisabled) setOpenStep(isExpanded ? -1 : step);
				};

				const wrappedComponent = cloneElement(component, {
					onSubmit: async (...args: []) => {
						await component.props.onSubmit?.(...args);
						if (isCompleted) setOpenStep(nextOpenStep, true);
					},
				});

				return (
					<QuestionStep stepIndex={step} key={getKey(heading)}>
						<Stack direction="row" align="start">
							<StepLabel isCompleted={isCompleted} isDisabled={isDisabled} step={step + 1} />
							<Stack align="center" direction="row">
								<Heading.H2 size="h3">{heading}</Heading.H2>
								{isEditable && !isDisabled && !isExpanded && (
									<Stack direction="row" justify="end" align="center">
										<Button size="sm" variant="plain" iconStart="pencil" onPress={toggleIsExpanded}>
											{/* todo: change to hard coded text until bank can use translations? */}
											{t(texts.common.change)}
										</Button>
									</Stack>
								)}
							</Stack>
						</Stack>
						<StepContent>
							<Stack gap={4}>
								<Markdown allowParagraphs>{description}</Markdown>
								<Expandable isExpanded={isExpanded}>{wrappedComponent}</Expandable>
							</Stack>
						</StepContent>
					</QuestionStep>
				);
			})}
		</Stack>
	);
};
